// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Pressable, StyleSheet, TextInput, Platform, ScrollView } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Text, View } from '@/components/Themed';
import { useUser } from './user-context';
import { useI18n } from '@/i18n';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { useRegisterUser } from '@/hooks/useRegisterUser';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { ensureCurrentPlayer } from '@/services/ensureCurrentPlayer';
import { suggestEmailDomain } from '@/services/suggestEmailDomain';
import { loginWithPassword } from '@/auth/oidc';
import {
  ActiveClaimSession,
  clearActiveClaimSession,
  getActiveClaimSession,
  subscribeClaimSession,
} from '@/services/claimSession';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';

const REGISTER_HERO_IMAGE_OPTIONS = {
  width: 1200,
  height: 474,
  bucketStep: 1,
} as const;

export default function RegisterScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { user, setUser } = useUser();
  const router = useRouter();
  const { debug: debugParam } = useLocalSearchParams<{ debug?: string }>();
  const debugEnabled = String(debugParam ?? '').toLowerCase() === 'true';
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const loginHeroImage = useMemo(
    () => getAssetImageSource('images/login-hero.png', '', '/register', REGISTER_HERO_IMAGE_OPTIONS),
    []
  );

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [claimSession, setClaimSession] = useState<ActiveClaimSession | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getActiveClaimSession().then((session) => {
      if (!cancelled) setClaimSession(session);
    });
    const unsubscribe = subscribeClaimSession((session) => setClaimSession(session));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const claimGuestMode = !user && !!claimSession?.claimAccessToken;

  useEffect(() => {
    if (!claimGuestMode) return;
    const guestName = claimSession?.sourcePlayerName?.trim();
    if (!guestName) return;
    setDisplayName((current) => (current.trim() ? current : guestName));
  }, [claimGuestMode, claimSession?.sourcePlayerName]);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const { loading: redirecting, handleRedirect } = useAuthRedirect({
    user,
    setUser,
    onError: setRedirectError,
    debug: debugEnabled,
  });

  const handleRegistrationSuccess = useCallback(async () => {
    setRedirectError(null);
    const loginEmail = email.trim();
    const claimedRoundId = claimGuestMode ? claimSession?.startedRoundId ?? null : null;
    if (claimGuestMode) {
      await clearActiveClaimSession();
    }
    try {
      const auth = await loginWithPassword(loginEmail, password);
      useRoundState.getState().clear();
      setUser({
        id: auth.id,
        forename: auth.name,
        token: auth.accessToken,
        idToken: auth.idToken ?? null,
        refreshToken: auth.refreshToken ?? null,
      });

      // The player record is created asynchronously from a CreatePlayerCommand, so warm it up
      // here rather than letting the user's first action after signup race it and fail. Never
      // block on the outcome — a slow player-service must not strand them on the register form.
      try {
        await ensureCurrentPlayer(apiBaseUrl, auth.accessToken, auth.id, {
          timeoutMs: 10_000,
          intervalMs: 400,
          retryWhileMissing: true,
        });
      } catch {
        // Aborted or unavailable — the screens that need the player retry on their own.
      }

      router.replace(claimedRoundId ? `/round/${claimedRoundId}/scorecard` : '/welcome');
      return;
    } catch {
      // Fall back to browser-based OIDC flow if direct grant is disabled or fails.
    }

    await handleRedirect();
  }, [apiBaseUrl, claimGuestMode, claimSession?.startedRoundId, email, handleRedirect, password, router, setUser]);

  const { status, error, loading, socketState, canRecoverByLogin, sendCreateRequest } = useRegisterUser({
    apiBaseUrl,
    isFocused,
    displayName,
    email,
    password,
    claimToken: claimGuestMode ? claimSession?.claimAccessToken ?? null : null,
    onReset: () => {
      setDisplayName('');
      setEmail('');
      setPassword('');
      setEmailSuggestion(null);
    },
    onSuccess: () => {
      void handleRegistrationSuccess();
    },
  });

  const effectiveError = redirectError ?? error;
  const isBusy = loading || redirecting;

  // Non-blocking: the address is never verified, and it is both the username and the only
  // password-reset route, so a typo here is an account nobody can recover. Offer the fix,
  // never insist on it.
  const handleEmailBlur = useCallback(() => {
    setEmailSuggestion(suggestEmailDomain(email));
  }, [email]);

  const applyEmailSuggestion = useCallback(() => {
    if (!emailSuggestion) return;
    setEmail(emailSuggestion);
    setEmailSuggestion(null);
  }, [emailSuggestion]);

  const handleCreatePress = useCallback(() => {
    setRedirectError(null);
    sendCreateRequest();
  }, [sendCreateRequest]);

  const handleWebSubmit = useCallback((event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();
    handleCreatePress();
  }, [handleCreatePress]);

  if (!isNavigationReady) return null;
  if (user) return <Redirect href="/welcome" />;

  const card = (
    <AuthStatusCard
      style={styles.card}
      title={t('register.cardTitle')}
      status={status}
      error={effectiveError}
      buttonLabel={isBusy ? t('register.creating') : t('register.createCta')}
      onPress={handleCreatePress}
      disabled={isBusy}
    >
      {claimGuestMode ? (
        <Text style={styles.claimNotice} testID="register-claim-adopt-notice">
          {t('register.claimAdoptNotice', { name: claimSession?.sourcePlayerName ?? '' })}
        </Text>
      ) : null}
      <Text style={styles.label}>{t('register.displayNameLabel')}</Text>
      <TextInput
        value={displayName}
        onChangeText={setDisplayName}
        placeholder={t('register.displayNamePlaceholder')}
        style={styles.input}
        autoCapitalize="words"
        autoComplete="nickname"
        placeholderTextColor="#8FA08B"
      />
      <Text style={styles.label}>{t('register.emailLabel')}</Text>
      <TextInput
        value={email}
        onChangeText={(next) => {
          setEmail(next);
          setEmailSuggestion(null);
        }}
        onBlur={handleEmailBlur}
        placeholder={t('register.emailPlaceholder')}
        style={styles.input}
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="email"
        placeholderTextColor="#8FA08B"
      />
      {emailSuggestion ? (
        <View style={styles.suggestionRow} testID="register-email-suggestion">
          <Text style={styles.suggestionText}>
            {t('register.emailSuggestion', { email: emailSuggestion })}
          </Text>
          <View style={styles.suggestionActions}>
            <Pressable
              style={styles.linkSmall}
              testID="register-email-suggestion-apply"
              accessibilityRole="button"
              onPress={applyEmailSuggestion}
            >
              <Text style={styles.suggestionApplyText}>{t('register.emailSuggestionApply')}</Text>
            </Pressable>
            <Pressable
              style={styles.linkSmall}
              testID="register-email-suggestion-dismiss"
              accessibilityRole="button"
              onPress={() => setEmailSuggestion(null)}
            >
              <Text style={styles.suggestionDismissText}>{t('register.emailSuggestionDismiss')}</Text>
            </Pressable>
          </View>
        </View>
      ) : null}
      <Text style={styles.label}>{t('register.passwordLabel')}</Text>
      <TextInput
        value={password}
        onChangeText={setPassword}
        placeholder={t('register.passwordPlaceholder')}
        style={styles.input}
        secureTextEntry
        autoComplete="new-password"
        placeholderTextColor="#8FA08B"
      />
      {canRecoverByLogin ? (
        <Pressable
          style={styles.recoverRow}
          testID="register-login-instead"
          accessibilityRole="button"
          onPress={() => router.replace('/')}
        >
          <Text style={styles.recoverText}>{t('register.loginInsteadCta')}</Text>
        </Pressable>
      ) : null}
      {/* Acceptance is implicit in pressing Create — a separate checkbox was one more tap and
          was never recorded server-side, so it proved nothing. Both documents stay one tap
          away, as full pages rather than a summarising modal. */}
      <Text style={styles.termsLabel} testID="register-terms-notice">
        {t('register.termsNotice')}
      </Text>
      <View style={styles.legalLinks}>
        <Pressable
          style={styles.linkSmall}
          testID="register-terms-link"
          accessibilityRole="link"
          onPress={() => router.push('/terms')}
        >
          <Text style={styles.linkSmallText}>{t('register.openTermsCta')}</Text>
        </Pressable>
        <Pressable
          style={styles.linkSmall}
          testID="register-privacy-link"
          accessibilityRole="link"
          onPress={() => router.push('/privacy')}
        >
          <Text style={styles.linkSmallText}>{t('register.openPrivacyCta')}</Text>
        </Pressable>
      </View>
      {debugEnabled ? (
        <Text style={styles.connection}>
          {t('register.connectionLabel', { status: socketState === 'connected' ? t('register.connectionUp') : t('register.connectionDown') })}
        </Text>
      ) : null}
      <Pressable style={styles.link} onPress={() => router.replace('/')}>
        <Text style={styles.linkText}>{t('register.backToLogin')}</Text>
      </Pressable>
    </AuthStatusCard>
  );

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <Image source={loginHeroImage} style={styles.hero} resizeMode="contain" />
      {Platform.OS === 'web'
        ? <form onSubmit={handleWebSubmit} style={styles.webForm as never}>{card}</form>
        : card}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    alignItems: 'center',
    gap: 12,
  },
  scroll: { flex: 1, backgroundColor: '#011806' },
  hero: {
    width: '100%',
    maxWidth: 720,
    aspectRatio: 1200 / 474,
    marginTop: 20,
  },
  card: {
    width: '100%',
    maxWidth: 900,
  },
  webForm: {
    width: '100%',
    maxWidth: 900,
  },
  label: {
    color: appColors.text.primary,
    fontWeight: '700',
    marginBottom: 6,
    marginTop: 10,
  },
  claimNotice: {
    color: '#A1D493',
    backgroundColor: '#0C2510',
    borderColor: '#1A3A20',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 6,
    lineHeight: 20,
  },
  input: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    backgroundColor: '#0C2510',
  },
  termsLabel: {
    marginTop: 10,
    color: '#8FA08B',
    fontSize: 12,
    flexShrink: 1,
  },
  suggestionRow: {
    marginTop: -4,
    marginBottom: 4,
    backgroundColor: 'transparent',
  },
  suggestionText: {
    color: appColors.text.primary,
    fontSize: 13,
  },
  suggestionActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 6,
    backgroundColor: 'transparent',
  },
  suggestionApplyText: {
    color: '#A1D493',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  suggestionDismissText: {
    color: '#8FA08B',
    fontWeight: '600',
  },
  recoverRow: {
    marginTop: 8,
    alignSelf: 'flex-start',
    backgroundColor: 'transparent',
  },
  recoverText: {
    color: '#A1D493',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  legalLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    backgroundColor: 'transparent',
  },
  linkSmall: {
    marginTop: -4,
    alignSelf: 'flex-start',
  },
  linkSmallText: {
    color: '#A1D493',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  connection: {
    color: '#8FA08B',
    fontSize: 12,
    marginTop: 12,
  },
  link: {
    marginTop: 12,
    alignItems: 'center',
  },
  linkText: {
    color: '#A1D493',
    fontWeight: '700',
  },
});
